import { IconMail, IconUser } from "@tabler/icons-react";
import { Upload, notification } from "antd";

import { Button } from "@mantine/core";
import { Input } from "@mantine/core";
import SFC_logo from "/Logo/SFC_logo.jpg";
import { apiUserUpload } from "@/api";
import { authClient } from "@/lib/auth-clients";
import { orpcClient } from "@/lib/orpc-client";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface CompanyData {
  business?: string;
  name?: string;
  businessIdNumber?: string;
  documentId?: string;
  capitalAmount?: string;
  companyAbbreviation?: string;
  companyRepresentative?: string;
  contact?: string;
  department?: string;
  email?: string;
  contactDepartment?: string;
  contactTelephone?: string;
  fax?: string;
  invitationCode?: string;
  invoice?: string;
  localPhoneNumber?: string;
  fullAddress?: {
    city?: string;
    district?: string;
    postalCode?: string;
    address?: string;
  };
  telephone?: string;
  website?: string;
  logo?: string;
  users?: string;
}

function AddCompanyListPage() {
  const { t } = useTranslation();
  const [companyData, setCompanyData] = useState<CompanyData>({});
  const [uploadImg, setUploadImg] = useState("");
  const [uploadState, setUploadState] = useState(false);
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const handleChangeCompanyData = (value: string, type: string) => {
    setCompanyData((prevData) => ({
      ...prevData,
      [type]: value,
    }));
  };

  const handleChangeImg = async (info: any) => {
    setUploadState(true);
    if (!uploadState) {
      const file = info.file.originFileObj;

      const formData = new FormData();
      formData.append("files", file, file.name);
      formData.append("name", info.file.name);

      const fileInfo = {
        alternativeText: localStorage.getItem("companyId"),
      };
      formData.append("fileInfo", JSON.stringify(fileInfo));

      try {
        let result = await apiUserUpload(formData);

        if (result[0]?.id) {
          setUploadImg(URL.createObjectURL(file));
          handleChangeCompanyData(result[0].id, "logo");
          api.success({
            message: `Notification`,
            description: <div>{t("companyInfo.logoUploadSuccess")}</div>,
            placement: "topLeft",
            duration: 3,
          });
        } else {
          api.error({
            message: `Notification`,
            description: <div>{t("companyInfo.logoUploadError")}</div>,
            placement: "topLeft",
            duration: 0,
          });
          console.error(`apiUserUpload went wrong ${result}`);
        }
      } catch (error) {
        api.error({
          message: `Notification`,
          description: <div>{t("companyInfo.logoUploadError")}</div>,
          placement: "topLeft",
          duration: 0,
        });
        console.error("Error uploading logo:", error);
      }
      setUploadState(false);
    }
  };

  const handleChangeAddressData = (value: string, type: string) => {
    setCompanyData((prevData) => ({
      ...prevData,
      fullAddress: {
        ...prevData?.fullAddress,
        [type]: value,
      },
    }));
  };

  const handleSubmit = async (data: CompanyData) => {
    try {
      const companyName = data.name || "New Company";
      const slug = crypto.randomUUID();

      const orgResult = await authClient.organization.create({
        name: companyName,
        slug,
      });

      if (orgResult.error) {
        api.error({
          message: `Notification`,
          description: (
            <div>
              {orgResult.error.message || t("companyInfo.errorUpdatingData")}
            </div>
          ),
          placement: "topLeft",
          duration: 0,
        });
        console.error("Organization creation error:", orgResult.error);
        return;
      }

      if (orgResult.data) {
        await authClient.organization.setActive({
          organizationId: orgResult.data.id,
        });

        const { name, users, fullAddress, capitalAmount, ...updateData } = data;

        const transformedAddress = fullAddress
          ? {
              ...fullAddress,
              postalCode: fullAddress.postalCode
                ? Number(fullAddress.postalCode)
                : undefined,
            }
          : undefined;

        if (
          Object.keys(updateData).length > 0 ||
          transformedAddress ||
          capitalAmount
        ) {
          await orpcClient.company.update({
            ...updateData,
            ...(transformedAddress && { address: transformedAddress }),
            ...(capitalAmount && { capitalAmount: Number(capitalAmount) }),
          });
        }

        api.success({
          message: `Notification`,
          description: <div>{t("companyInfo.saveSuccess")}</div>,
          placement: "topLeft",
          duration: 3,
        });

        setTimeout(() => {
          navigate(`/hrMainPage/companyInfo`);
        }, 1000);
      }
    } catch (error) {
      api.error({
        message: `Notification`,
        description: <div>{t("companyInfo.errorUpdatingData")}</div>,
        placement: "topLeft",
        duration: 0,
      });
      console.error("Error creating company:", error);
    }
  };

  const {
    business,
    name,
    businessIdNumber,
    capitalAmount,
    companyAbbreviation,
    companyRepresentative,
    contact,
    contactDepartment,
    contactTelephone,
    email,
    fax,
    invitationCode,
    invoice,
    localPhoneNumber,
    fullAddress,
    website,
  } = companyData || {};

  const { city, district, postalCode, address } = fullAddress || {};

  return (
    <div className="flex h-full items-center justify-center p-14">
      {contextHolder}
      <div className="w-[700px] rounded-xl bg-white px-16">
        <>
          <img src={SFC_logo} />
          <div className="text-6xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("addCompany.title") || "新增公司"}
          </div>
          <div style={{ color: "#6F6F6F" }} className="py-8">
            {t("addCompany.description") ||
              "建立公司資訊，節省您在管理時所花費的時間。建立只需兩分鐘。"}
          </div>
          <div className="text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("addCompany.basicInfo") || "基本資訊"}
          </div>
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>
              {t("addCompany.companyInfo") || "公司資訊"}
            </div>
            <Input
              placeholder="Input component"
              leftSection={<IconMail size={16} />}
              onChange={(e) => handleChangeCompanyData(e.target.value, "name")}
              value={name}
            />
          </div>
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>
              {t("addCompany.businessIdNumber") || "統一編號"}
            </div>
            <Input
              placeholder="Input component"
              leftSection={<IconMail size={16} />}
              value={businessIdNumber}
              onChange={(e) =>
                handleChangeCompanyData(e.target.value, "businessIdNumber")
              }
            />
          </div>
          <div className="flex items-center justify-center">
            <div className="py-4">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.representative") || "負責人名稱"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={companyRepresentative}
                onChange={(e) =>
                  handleChangeCompanyData(
                    e.target.value,
                    "companyRepresentative"
                  )
                }
              />
            </div>

            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.business") || "營業項目"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={business}
                onChange={(e) =>
                  handleChangeCompanyData(e.target.value, "business")
                }
              />
            </div>
            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.capital") || "資本額"}
              </div>
              <Input
                placeholder="Input component"
                value={capitalAmount}
                leftSection={<IconMail size={16} />}
                onChange={(e) =>
                  handleChangeCompanyData(e.target.value, "capitalAmount")
                }
              />
            </div>
          </div>
          <div className="text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("addCompany.contactInfo") || "聯絡資訊"}
          </div>
          <div className="flex items-center justify-center">
            <div className="py-4">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.localPhone") || "市內電話"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={localPhoneNumber}
                onChange={(e) =>
                  handleChangeCompanyData(e.target.value, "localPhoneNumber")
                }
              />
            </div>

            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.fax") || "傳真"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={fax}
                onChange={(e) => handleChangeCompanyData(e.target.value, "fax")}
              />
            </div>
          </div>
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>
              {t("companyInfo.email") || "Email"}
            </div>
            <Input
              placeholder="Input component"
              leftSection={<IconMail size={16} />}
              value={email}
              onChange={(e) =>
                handleChangeCompanyData(e.target.value, "email")
              }
            />
          </div>
          <div className="flex items-center justify-center">
            <div className="py-4">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.contact") || "聯絡人"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={contact}
                onChange={(e) =>
                  handleChangeCompanyData(e.target.value, "contact")
                }
              />
            </div>

            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.phone") || "電話"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={contactTelephone}
                onChange={(e) =>
                  handleChangeCompanyData(e.target.value, "contactTelephone")
                }
              />
            </div>
            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.department") || "部門"}
              </div>
              <Input
                placeholder="Input component"
                value={contactDepartment}
                leftSection={<IconMail size={16} />}
                onChange={(e) =>
                  handleChangeCompanyData(e.target.value, "contactDepartment")
                }
              />
            </div>
          </div>

          <div className="text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("addCompany.contactAddress") || "聯絡地址"}
          </div>
          <div className="flex items-center justify-center">
            <div className="py-4">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.city") || "縣市"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={city}
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "city")
                }
              />
            </div>

            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.district") || "行政區"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={district}
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "district")
                }
              />
            </div>
            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.postalCode") || "郵遞區號"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={postalCode}
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "postalCode")
                }
              />
            </div>
            <div className="py-4">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.address") || "地址"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={address}
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "address")
                }
              />
            </div>
          </div>

          <div className="text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("addCompany.advancedSettings") || "進階設定"}
          </div>

          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>
              {t("addCompany.logo") || "Logo"}
            </div>
            <div className="flex items-center justify-start">
              <div className="flex py-2 align-center">
                <Upload
                  name="logo"
                  showUploadList={false}
                  onChange={handleChangeImg}
                  accept="image/*"
                >
                  <Button variant="default" disabled={uploadState}>
                    {uploadState
                      ? t("companyInfo.uploading")
                      : t("companyInfo.uploadLogo")}
                  </Button>
                </Upload>
              </div>
              {uploadImg && (
                <div className="ml-4">
                  <img
                    src={uploadImg}
                    alt="Company Logo"
                    style={{
                      width: "60px",
                      height: "60px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>
              {t("addCompany.website") || "網站"}
            </div>
            <Input
              placeholder="Input component"
              leftSection={<IconMail size={16} />}
              value={website}
              onChange={(e) =>
                handleChangeCompanyData(e.target.value, "website")
              }
            />
          </div>
          <div className="flex items-center justify-center">
            <div className="py-4">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.companyAbbreviation") || "公司簡稱"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={companyAbbreviation}
                onChange={(e) =>
                  handleChangeCompanyData(e.target.value, "companyAbbreviation")
                }
              />
            </div>
            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("addCompany.invoiceTitle") || "發票抬頭"}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconMail size={16} />}
                value={invoice}
                onChange={(e) =>
                  handleChangeCompanyData(e.target.value, "invoice")
                }
              />
            </div>
          </div>

          <div className="text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("addCompany.invitationCode") || "邀請碼"}
          </div>

          <div className="py-4 pl-2">
            <div style={{ color: "#6F6F6F" }}>
              {t("addCompany.enterInvitationCode") || "輸入邀請碼"}
            </div>
            <Input
              placeholder="Input component"
              leftSection={<IconMail size={16} />}
              value={invitationCode}
              onChange={(e) =>
                handleChangeCompanyData(e.target.value, "invitationCode")
              }
            />
          </div>

          <div className="flex">
            <Button
              variant="default"
              style={{ width: "100%", margin: "1rem" }}
              radius="md"
              onClick={() => navigate("/companyListPage")}
            >
              {t("addCompany.cancel") || "取消"}
            </Button>
            <Button
              variant="filled"
              color="gray"
              style={{ width: "100%", margin: "1rem 0" }}
              radius="md"
              onClick={() => handleSubmit(companyData)}
            >
              {t("hrMain.add") || "新增"}
            </Button>
          </div>
        </>
      </div>
    </div>
  );
}

export default AddCompanyListPage;
